import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import BaseTabs from "@/components_v2/Tabs/BaseTabs";
import EvidenceTable from "@/domains/evidence/components/EvidenceTable";
import { UserContentProps } from "@/features/users/profile/types";
import { useUserPermissions } from "@/features/users/hooks/useUserPermissions";
import Analytics from "./Analytics";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import ProfileTypeForms from "@/features/users/profile/components/ProfileTypeForms";
import DiscussionList from "@/domains/community/components/DiscussionList";
import CommentList from "@/domains/community/components/CommentList";
import UserProfileDetailsDisplay from "@/features/users/profile/components/UserProfileDetailsDisplay";
import UserProfileDetailsForm from "@/features/users/profile/components/UserProfileDetailsForm";
import { useResponsive } from "@/shared/hooks/useBreakpoints";

const UserProfileContainer = ({ user }: UserContentProps) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const currentUser = useSelector(selectCurrentUser);
  const permissions = useUserPermissions(user, currentUser);
  const [isEdit, setIsEdit] = useState(false);

  const handleEdit = () => {
    setIsEdit(true);
  };

  const tabs = useMemo(() => {
    if (!user) return [];

    return [
      {
        canView: permissions.canViewOverviewTab,
        key: "overview",
        label: t("users.labels.overview"),
        children: <Analytics user={user} />,
      },
      {
        canView: permissions.canViewProfileFormsTab,
        key: "about",
        label: t("users.labels.about"),
        children: <ProfileTypeForms user={user} />,
      },
      {
        canView: permissions.canViewEvidenceTab,
        key: "evidence",
        label: t("users.labels.evidence"),
        children: (
          <EvidenceTable
            columnNames={[
              "title",
              "type",
              "contributors",
              "sharingRights",
              "createdAt",
            ]}
            filterNames={[
              "type",
              "sharingRights",
              "contributors",
              "languages",
              "search",
            ]}
            defaultFilters={{
              user: user.id,
              editable: true,
            }}
          />
        ),
      },
      {
        canView: permissions.canViewDiscussionsTab,
        key: "discussions",
        label: t("users.labels.discussions"),
        children: (
          <DiscussionList
            defaultFilters={{
              createdBy: user.id,
            }}
          />
        ),
      },
      {
        canView: permissions.canViewCommentsTab,
        key: "comments",
        label: t("users.labels.comments"),
        children: (
          <CommentList
            defaultFilters={{
              createdBy: user.id,
            }}
          />
        ),
      },
    ];
  }, [permissions, user]);

  return (
    <div className={`flex gap-400 ${isMobile ? "flex-col" : ""}`}>
      <UserProfileDetailsDisplay user={user} onEdit={handleEdit} />
      <UserProfileDetailsForm user={user} open={isEdit} setOpen={setIsEdit} />

      <BaseTabs
        defaultActiveKey="overview"
        items={tabs.filter((tab) => tab.canView)}
      />
    </div>
  );
};

export default UserProfileContainer;
